/**
 * Réglages propres à un fond : description (le « schéma »), valeurs d'origine, vérification et bornes.
 * Rien d'autre que du calcul, pour pouvoir le tester seul.
 *
 * Chaque fond décrit ses réglages dans un fichier `reglages.js` de son dossier : un objet {identifiant: description}.
 * Le tableau de bord fabrique tout seul les curseurs et cases à partir de cette description, le fond reçoit ses valeurs
 * déjà vérifiées par `resoudre`, et le serveur stocke les valeurs sans rien connaître des fonds.
 *
 * Trois types de réglages :
 *   {type: "nombre", libelle, aide?, min, max, pas?, defaut, unite?}      curseur ; `unite` s'écrit après la valeur (« × », « % »)
 *   {type: "interrupteur", libelle, aide?, defaut}                          case à cocher
 *   {type: "choix", libelle, aide?, options: [{valeur, libelle}], defaut}   liste déroulante
 */

/** Nombre de décimales de `pas` (0,1 donne 1 ; 5 donne 0), pour arrondir sans résidu de calcul (0,30000000000000004). */
const decimales = pas => (String(pas).split(".")[1] || "").length;

function resoudreUn(description, valeur) {
    switch (description.type) {
        case "nombre": {
            const nombre = typeof valeur === "number" || (typeof valeur === "string" && valeur.trim() !== "") ? Number(valeur) : NaN;
            if (!Number.isFinite(nombre)) return description.defaut;
            const pas = description.pas || 1;
            const borne = Math.max(description.min, Math.min(description.max, nombre));
            const arrondi = Number((description.min + Math.round((borne - description.min) / pas) * pas).toFixed(decimales(pas)));
            return Math.max(description.min, Math.min(description.max, arrondi));
        }
        case "interrupteur":
            return typeof valeur === "boolean" ? valeur : description.defaut;
        case "choix":
            return description.options.some(o => o.valeur === valeur) ? valeur : description.defaut;
        default:
            return description.defaut;
    }
}

/** Valeurs d'origine de tous les réglages d'un fond. */
export function valeursParDefaut(schema = {}) {
    return Object.keys(schema).reduce((acc, cle) => ({...acc, [cle]: schema[cle].defaut}), {});
}

/**
 * Valeurs utilisables par le fond : tous les réglages du schéma, chacun ramené dans ses bornes ; une valeur absente,
 * d'un mauvais type ou hors des choix possibles retombe sur la valeur d'origine ; un réglage inconnu est ignoré.
 */
export function resoudre(schema = {}, brut) {
    const valeurs = brut && typeof brut === "object" ? brut : {};
    return Object.keys(schema).reduce((acc, cle) => ({...acc, [cle]: resoudreUn(schema[cle], valeurs[cle])}), {});
}

/** Vrai si les deux jeux de valeurs (une fois résolus) sont identiques. */
export function memesValeurs(schema, a, b) {
    const ra = resoudre(schema, a), rb = resoudre(schema, b);
    return Object.keys(schema).every(cle => ra[cle] === rb[cle]);
}

/**
 * Valeurs à enregistrer sur le serveur : seulement celles qui diffèrent de la valeur d'origine. Ainsi le serveur reste
 * léger, et un fond dont la valeur d'origine change un jour se met à jour chez ceux qui n'avaient rien réglé.
 * Rend null s'il n'y a rien à enregistrer (retour aux valeurs d'origine).
 */
export function aEnregistrer(schema, valeurs) {
    const resolu = resoudre(schema, valeurs);
    const modifies = Object.keys(schema).filter(cle => resolu[cle] !== schema[cle].defaut);
    return modifies.length ? modifies.reduce((acc, cle) => ({...acc, [cle]: resolu[cle]}), {}) : null;
}
